import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { CruiseConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Travelers } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { travelersStateOnConfig } from "../global/travelers/travelersState";
import { TravelersState } from "stores/wizard/state/global";

export class CruiseWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  @computed
  public get location() {
    return this.globalState.location;
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  @observable
  public config: CruiseConfig;

  @observable
  public subLOBState: string;

  @observable
  public destination3pp = "";

  @observable
  public cruiseLine3pp = "";

  @observable
  public duration3pp = "";

  @observable
  public departurePort3pp = "";

  @observable
  public dateRange3pp = "";

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    return false;
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateRange3pp(value: string) {
    this.dateRange3pp = value;
  }

  @action
  public updateDestination3pp(value: string) {
    this.destination3pp = value;
  }

  @action
  public updateDuration3pp(value: string) {
    this.duration3pp = value;
  }

  @action
  public updateDeparturePort3pp(value: string) {
    this.departurePort3pp = value;
  }

  @action
  public updateCruiseLine3pp(value: string) {
    this.cruiseLine3pp = value;
  }

  @observable
  public cruiseTravelers: TravelersState;

  @computed
  public get travelers() {
    return this.cruiseTravelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return travelersMetadata(this.travelers.nonHotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers, this.travelers);
  };

  @observable
  public cruiseDestination = "";

  @action
  public setCruiseDestination = (destination: string) => {
    this.cruiseDestination = destination;
  };

  @observable
  public dateStartInvalidKey = "";

  @observable
  public dateEndInvalidKey = "";

  @observable
  public destinationInvalidKey: string | "";

  @observable
  public nonHotelTravelersInvalidKey = "";

  @action
  public validateLessThanNTravelers = () => {
    if (!this.globalState.validateLessThanNTravelers(this.nonHotelTravelersMetadata, this.config.travelers)) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateDestinationSelection(selection);
  };

  @action
  public resetValidations = () => {
    this.nonHotelTravelersInvalidKey = "";

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  public validateForm = () => {
    const validations: boolean[] = [this.validateTravelersField()];

    return !validations.includes(false);
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    }
    this.globalState.submit();
  };

  public submit3pp = () => {
    this.globalState.submit();
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: CruiseConfig, globalState: GlobalWizardState) {
    this.config = config;
    this.cruiseTravelers = travelersStateOnConfig(this.config.travelers);
    this.globalState = globalState;
  }
}
